'use client';

import type { IconAnimateMode, IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { toggleButtonVariants } from '@magicschool/spellbook/components/ToggleButton';
import { ToggleIconButtonSpinnerSlot } from '@magicschool/spellbook/components/ToggleIconButton/slots';
import { useAnimatedIcon } from '@magicschool/spellbook/hooks/useAnimatedIcon';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import * as TogglePrimitive from 'radix-ui/toggle';
import { memo } from 'react';

export type ToggleIconButtonProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  Pick<IconProps, 'name'> &
  Omit<VariantProps<typeof toggleButtonVariants>, 'justify' | 'fullWidth' | 'isLoading'> & {
    /** If true, the component will render its children directly without wrapping in a Radix Toggle. This allows you to use a custom component (e.g., a link) while still getting toggle behavior and ARIA attributes. */
    asChild?: boolean;
    /** Screen reader text (required for accessibility) — provide via `label` prop OR wrap in `Tooltip` with `mode="label"`. */
    label?: string;
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    /** If true, the button will override the default icon with a custom icon. */
    overrideDefault?: boolean;
    /** Animate the icon on hover and focus of the button. `'hover'` plays on hover or focus; `'mount'` plays on mount; `false` explicitly disables. Only valid for `AnimatedIconName` values. */
    animate?: IconAnimateMode;
  };

type ToggleIconButtonContentProps = Pick<
  ToggleIconButtonProps,
  'name' | 'size' | 'label' | 'children' | 'isLoading' | 'overrideDefault' | 'animate'
> & {
  animating?: boolean;
};

const ToggleIconButtonContent = memo<ToggleIconButtonContentProps>(
  ({ animate, animating, children, isLoading, name, label, overrideDefault, size: providedSize }) => {
    const { slots, rest } = useSlots(children, {
      loadingIcon: {
        Component: ToggleIconButtonSpinnerSlot,
      },
    });

    if (isLoading) {
      return slots.loadingIcon;
    }

    const size = getControlIconSize(providedSize);

    return overrideDefault ? (
      children
    ) : (
      <>
        <Icon name={name} size={size} animate={animate} animating={animating} />
        {(label || rest) && <span className="sr-only">{label || rest}</span>}
      </>
    );
  },
);

export const ToggleIconButtonBase: React.FC<ToggleIconButtonProps> = ({
  animate,
  label,
  children,
  isLoading = false,
  withBorder = false,
  size = 'md',
  shape = 'default',
  variant = 'default',
  color = 'primary',
  className,
  disabled,
  name,
  defaultPressed,
  pressed,
  onPressedChange,
  onClick,
  overrideDefault = false,
  ...props
}) => {
  const isActuallyDisabled = disabled || isLoading;
  const resolvedAnimate = isActuallyDisabled ? undefined : animate;
  const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);

  const handleOnPressedChange = (pressed: boolean) => {
    onPressedChange?.(pressed);
  };

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isActuallyDisabled) {
      // explicitly prevent default and stop propagation so that `asChild` components are also disabled
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  return (
    <TogglePrimitive.Root
      data-spellbook-name="toggle-icon-button"
      className={cn(toggleButtonVariants({ variant, size, shape, withBorder, icon: true, color }), className)}
      defaultPressed={defaultPressed}
      pressed={pressed}
      onPressedChange={handleOnPressedChange}
      onClick={handleOnClick}
      aria-disabled={isActuallyDisabled}
      aria-busy={isLoading}
      {...props}
      {...getInteractProps(props)}
    >
      <ToggleIconButtonContent
        name={name}
        size={size}
        label={label}
        isLoading={isLoading}
        overrideDefault={overrideDefault}
        animate={resolvedAnimate}
        animating={animating}
      >
        {children}
      </ToggleIconButtonContent>
    </TogglePrimitive.Root>
  );
};

/**
 * Icon-only toggle button for binary on/off states. Provides `aria-pressed` for accessibility and visual feedback for selected state.
 *
 * ### When to use
 * - For binary on/off actions where the state persists (favorite, bookmark, like)
 * - For toolbar formatting toggles (bold, italic, underline)
 * - For compact UI where a text label doesn't fit but the toggle state needs to be communicated
 * - When the icon clearly communicates both the action and current state (filled heart = favorited)
 * - When pairing with `ToggleButton` for responsive layouts (icon-only on mobile, text on desktop)
 *
 * ### When NOT to use
 * - For one-time actions: use `IconButton` which doesn't communicate toggle state
 * - For actions with visible text: use `ToggleButton` which supports text labels
 * - For primary actions: use `Button` with visible text for clarity
 * - For non-binary states (e.g., three-way toggle): use a different pattern
 * - For settings that persist after leaving the page: use `Switch` instead — `Switch` is for settings, `ToggleIconButton` is for modes/actions
 *
 * ### API
 * - `name`: Lucide icon name (required) — see https://lucide.dev for available icons
 * - `label`: Screen reader text (required for accessibility) — provide via `label` prop OR wrap in `Tooltip` with `mode="label"`
 * - `pressed`: Controlled pressed state — `true` for on, `false` for off
 * - `defaultPressed`: Initial pressed state for uncontrolled usage
 * - `onPressedChange`: Callback when pressed state changes — receives new `boolean` state
 * - `variant`: Visual style — only `default` is available
 * - `color`: Semantic color when pressed: `primary` (default), `success`
 * - `size`: Button dimensions: `xs`, `sm`, `md` (default), `lg`
 * - `shape`: Border radius: `default` (rounded square), `circle` (fully rounded), `square` (no rounding)
 * - `withBorder`: Adds border styling — useful for button groups or increased visual distinction
 * - `isLoading`: Shows spinner, disables clicks, and sets `aria-busy="true"`
 * - `asChild`: If true, the component will render its children directly without wrapping in a Radix Toggle. This allows you to use a custom component (e.g., a link) while still getting toggle behavior and ARIA attributes.
 *
 * ### Composition (slots)
 * - `<ToggleIconButton.Spinner>`: Loading indicator. Rendered automatically when `isLoading={true}` — no need to include explicitly.
 *
 * ### Visual states
 * - **Unpressed**: Default appearance with no background highlight
 * - **Pressed**: Background fill indicates active/on state; `aria-pressed="true"` set automatically via Radix Toggle
 * - **Hover**: Subtle background appears on hover
 * - **Loading**: Spinner replaces icon, pointer events blocked, `aria-busy="true"` set
 * - **Disabled**: Icon uses disabled color, cursor shows not-allowed, `aria-disabled="true"` set
 * - **Focus**: Focus ring appears around button for keyboard navigation
 *
 * ### Accessibility
 * - **REQUIRED**: Provide accessible label via `label` prop OR wrap in `Tooltip` with `mode="label"`. The `label` prop renders as `sr-only` text (visually hidden, announced by screen readers).
 * - Automatically sets `aria-pressed` via Radix Toggle primitive — announces "pressed" or "not pressed" to screen readers
 * - Uses `aria-disabled` instead of `disabled` attribute to maintain focusability for screen readers. Pass `disabled` prop and the component handles `aria-disabled` and appropriate styles.
 * - Sets `aria-busy="true"` during loading state to announce to assistive technology. Pass `isLoading` prop and the component handles ARIA attributes and styles.
 * - Click handlers are blocked when disabled or loading
 * - Keyboard: Enter/Space toggles the pressed state; focus is visible via ring outline
 *
 * ### Tooltip integration
 * ```tsx
 * <Tooltip mode="label">
 *   <Tooltip.Trigger>
 *     <ToggleIconButton name="star" pressed={isFavorite} onPressedChange={setIsFavorite} />
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</Tooltip.Content>
 * </Tooltip>
 * ```
 * When using `Tooltip` with `mode="label"`, the tooltip content provides the accessible name, so `label` prop is not needed.
 *
 * ### Additional notes
 * For Spellbook contributors: `ToggleIconButton` uses `toggleButtonVariants` from `ToggleButton` internally, so variant/color/size styling is shared. Changes to `toggleButtonVariants` affect all toggle button components.
 */
export const ToggleIconButton = Object.assign(ToggleIconButtonBase, {
  Spinner: ToggleIconButtonSpinnerSlot,
});
